import { createAppRequest } from "@/services/request";
import type { ApiRequestOptions } from "./types/common";

const request = createAppRequest();

export interface PurchaseApplyRecord {
  uuid: string;
  applyNo: string;
  applicantMemberUuid: string;
  warehouseUuid: string;
  purpose: string;
  remark?: string;
  status: string;
  ruleResult?: string;
  ruleExplanation?: string;
  submittedAt?: string;
  finishedAt?: string;
  createdAt: string;
}

export interface PurchaseOrderRecord {
  uuid: string;
  orderNo: string;
  applyUuid: string;
  supplierUuid?: string;
  supplierName?: string;
  warehouseUuid: string;
  status: string;
  totalAmount: string;
  arrivedQty: string;
  inboundQty: string;
  receivedAt?: string;
  finishedAt?: string;
  closedAt?: string;
  closeReason?: string;
  createdAt: string;
}

export interface PurchaseOrderItemRecord {
  uuid: string;
  orderUuid: string;
  applyItemUuid: string;
  materialUuid: string;
  warehouseUuid: string;
  quantity: string;
  arrivedQty: string;
  inboundQty: string;
  receivedQty: string;
  unitPrice: string;
  amount: string;
}

export interface PurchaseReceiptRecord {
  uuid: string;
  receiptNo: string;
  orderUuid: string;
  orderNo: string;
  warehouseUuid: string;
  supplierUuid?: string;
  supplierName?: string;
  receiptDate?: string;
  batchNo?: string;
  invoiceNo?: string;
  status: string;
  totalArrivalQty: string;
  totalInboundQty: string;
  totalAmount: string;
  registeredByMemberUuid?: string;
  inboundedByMemberUuid?: string;
  registeredAt?: string;
  inboundedAt?: string;
  remark?: string;
  createdAt: string;
}

export interface PurchaseReceiptItemRecord {
  uuid: string;
  receiptUuid: string;
  orderUuid: string;
  orderItemUuid: string;
  materialUuid: string;
  warehouseUuid: string;
  arrivalQty: string;
  inboundQty: string;
  unitPrice: string;
  amount: string;
  remark?: string;
}

export interface PurchaseSuggestionRecord {
  uuid: string;
  suggestionNo: string;
  warehouseUuid: string;
  materialUuid: string;
  currentQty: string;
  safetyStock: string;
  suggestQty: string;
  suggestDate?: string;
  reason: string;
  aiConfidence: string;
  status: string;
  convertedApplyUuid?: string;
  createdAt: string;
}

export interface PurchaseExceptionRecord {
  uuid: string;
  exceptionNo: string;
  orderUuid?: string;
  orderNo?: string;
  receiptUuid?: string;
  receiptNo?: string;
  materialUuid?: string;
  exceptionType: string;
  severity: string;
  status: string;
  exceptionContent: string;
  suggestion?: string;
  reportedByMemberUuid?: string;
  handledByMemberUuid?: string;
  handledAt?: string;
  handleComment?: string;
  createdAt: string;
}

export interface PurchaseApplyDetailRecord {
  apply: PurchaseApplyRecord;
  items: PurchaseApplyItemRecord[];
  orders: PurchaseOrderRecord[];
  receipts: PurchaseReceiptRecord[];
}

export interface PurchaseApplyItemRecord {
  uuid: string;
  applyUuid: string;
  materialUuid: string;
  warehouseUuid: string;
  quantity: string;
  suggestedUnitPrice: string;
}

export interface CreatePurchaseApplyPayload {
  warehouseUuid: string;
  purpose: string;
  remark?: string;
  items: Array<{
    materialUuid: string;
    quantity: number;
    suggestedUnitPrice?: number;
  }>;
}

export async function getPurchaseApplies(options?: ApiRequestOptions) {
  return request<PurchaseApplyRecord[]>("/bff/purchase/applies", {
    method: "GET",
    ...(options || {}),
  });
}

export async function createPurchaseApply(
  data: CreatePurchaseApplyPayload,
  options?: ApiRequestOptions
) {
  return request<PurchaseApplyRecord>("/bff/purchase/applies", {
    method: "POST",
    data,
    ...(options || {}),
  });
}

export async function createPurchaseApplyDraft(
  data: CreatePurchaseApplyPayload,
  options?: ApiRequestOptions
) {
  return request<PurchaseApplyRecord>("/bff/purchase/applies/drafts", {
    method: "POST",
    data,
    ...(options || {}),
  });
}

export async function getPurchaseApplyDetail(
  applyUuid: string,
  options?: ApiRequestOptions
) {
  return request<PurchaseApplyDetailRecord>(
    `/bff/purchase/applies/${applyUuid}`,
    {
      method: "GET",
      ...(options || {}),
    }
  );
}

export async function updatePurchaseApply(
  applyUuid: string,
  data: Partial<CreatePurchaseApplyPayload>,
  options?: ApiRequestOptions
) {
  return request<PurchaseApplyRecord>(`/bff/purchase/applies/${applyUuid}`, {
    method: "PUT",
    data,
    ...(options || {}),
  });
}

export async function submitPurchaseApply(
  applyUuid: string,
  options?: ApiRequestOptions
) {
  return request<PurchaseApplyRecord>(
    `/bff/purchase/applies/${applyUuid}/submit`,
    {
      method: "POST",
      data: {},
      ...(options || {}),
    }
  );
}

export async function cancelPurchaseApply(
  applyUuid: string,
  options?: ApiRequestOptions
) {
  return request<PurchaseApplyRecord>(`/bff/purchase/applies/${applyUuid}`, {
    method: "DELETE",
    ...(options || {}),
  });
}

export async function getPurchaseSuggestions(options?: ApiRequestOptions) {
  return request<PurchaseSuggestionRecord[]>("/bff/purchase/suggestions", {
    method: "GET",
    ...(options || {}),
  });
}

export async function generatePurchaseSuggestions(options?: ApiRequestOptions) {
  return request<PurchaseSuggestionRecord[]>(
    "/bff/purchase/suggestions/generate",
    {
      method: "POST",
      data: {},
      ...(options || {}),
    }
  );
}

export async function convertPurchaseSuggestion(
  suggestionUuid: string,
  options?: ApiRequestOptions
) {
  return request<PurchaseApplyRecord>(
    `/bff/purchase/suggestions/${suggestionUuid}/convert`,
    {
      method: "POST",
      data: {},
      ...(options || {}),
    }
  );
}

export async function ignorePurchaseSuggestion(
  suggestionUuid: string,
  options?: ApiRequestOptions
) {
  return request<PurchaseSuggestionRecord>(
    `/bff/purchase/suggestions/${suggestionUuid}/ignore`,
    {
      method: "POST",
      data: {},
      ...(options || {}),
    }
  );
}

export async function deferPurchaseSuggestion(
  suggestionUuid: string,
  options?: ApiRequestOptions
) {
  return request<PurchaseSuggestionRecord>(
    `/bff/purchase/suggestions/${suggestionUuid}/defer`,
    {
      method: "POST",
      data: {},
      ...(options || {}),
    }
  );
}

export async function createPurchaseOrder(
  applyUuid: string,
  data: { supplierUuid?: string; supplierName?: string } = {},
  options?: ApiRequestOptions
) {
  return request<PurchaseOrderRecord>(
    `/bff/purchase/applies/${applyUuid}/order`,
    {
      method: "POST",
      data,
      ...(options || {}),
    }
  );
}

export async function approvePurchaseApply(
  applyUuid: string,
  data: { comment?: string } = {},
  options?: ApiRequestOptions
) {
  return request<PurchaseApplyRecord>(
    `/bff/purchase/applies/${applyUuid}/approve`,
    {
      method: "POST",
      data,
      ...(options || {}),
    }
  );
}

export async function rejectPurchaseApply(
  applyUuid: string,
  data: { comment?: string } = {},
  options?: ApiRequestOptions
) {
  return request<PurchaseApplyRecord>(
    `/bff/purchase/applies/${applyUuid}/reject`,
    {
      method: "POST",
      data,
      ...(options || {}),
    }
  );
}

export async function getPurchaseOrders(options?: ApiRequestOptions) {
  return request<PurchaseOrderRecord[]>("/bff/purchase/orders", {
    method: "GET",
    ...(options || {}),
  });
}

export async function cancelPurchaseOrder(
  orderUuid: string,
  data: { reason?: string } = {},
  options?: ApiRequestOptions
) {
  return request<PurchaseOrderRecord>(
    `/bff/purchase/orders/${orderUuid}/cancel`,
    {
      method: "POST",
      data,
      ...(options || {}),
    }
  );
}

export async function closePurchaseOrder(
  orderUuid: string,
  data: { reason: string },
  options?: ApiRequestOptions
) {
  return request<PurchaseOrderRecord>(
    `/bff/purchase/orders/${orderUuid}/close`,
    {
      method: "POST",
      data,
      ...(options || {}),
    }
  );
}

export async function getPurchaseOrderItems(
  orderUuid: string,
  options?: ApiRequestOptions
) {
  return request<PurchaseOrderItemRecord[]>(
    `/bff/purchase/orders/${orderUuid}/items`,
    {
      method: "GET",
      ...(options || {}),
    }
  );
}

export async function getPurchaseReceipts(options?: ApiRequestOptions) {
  return request<PurchaseReceiptRecord[]>("/bff/purchase/receipts", {
    method: "GET",
    ...(options || {}),
  });
}

export async function getPurchaseReceiptItems(
  receiptUuid: string,
  options?: ApiRequestOptions
) {
  return request<PurchaseReceiptItemRecord[]>(
    `/bff/purchase/receipts/${receiptUuid}/items`,
    {
      method: "GET",
      ...(options || {}),
    }
  );
}

export async function registerPurchaseReceipt(
  orderUuid: string,
  data: {
    receiptDate?: string;
    batchNo?: string;
    invoiceNo?: string;
    remark?: string;
    items: Array<{
      orderItemUuid: string;
      arrivalQty: number;
      unitPrice?: number;
      remark?: string;
    }>;
  },
  options?: ApiRequestOptions
) {
  return request<PurchaseReceiptRecord>(
    `/bff/purchase/orders/${orderUuid}/receipts`,
    {
      method: "POST",
      data,
      ...(options || {}),
    }
  );
}

export async function confirmPurchaseReceiptInbound(
  receiptUuid: string,
  data: { remark?: string } = {},
  options?: ApiRequestOptions
) {
  return request<PurchaseReceiptRecord>(
    `/bff/purchase/receipts/${receiptUuid}/inbound`,
    {
      method: "POST",
      data,
      ...(options || {}),
    }
  );
}

export async function cancelPurchaseReceipt(
  receiptUuid: string,
  data: { reason?: string } = {},
  options?: ApiRequestOptions
) {
  return request<PurchaseReceiptRecord>(
    `/bff/purchase/receipts/${receiptUuid}/cancel`,
    {
      method: "POST",
      data,
      ...(options || {}),
    }
  );
}

export async function receivePurchaseOrder(
  orderUuid: string,
  options?: ApiRequestOptions
) {
  return request<PurchaseOrderRecord>(
    `/bff/purchase/orders/${orderUuid}/receive`,
    {
      method: "POST",
      data: {},
      ...(options || {}),
    }
  );
}

export async function getPurchaseExceptions(options?: ApiRequestOptions) {
  return request<PurchaseExceptionRecord[]>("/bff/purchase/exceptions", {
    method: "GET",
    ...(options || {}),
  });
}

export async function reportPurchaseException(
  data: {
    orderUuid?: string;
    receiptUuid?: string;
    materialUuid?: string;
    exceptionType: string;
    severity?: string;
    exceptionContent: string;
    suggestion?: string;
  },
  options?: ApiRequestOptions
) {
  return request<PurchaseExceptionRecord>("/bff/purchase/exceptions", {
    method: "POST",
    data,
    ...(options || {}),
  });
}

export async function handlePurchaseException(
  exceptionUuid: string,
  data: { status: string; comment?: string },
  options?: ApiRequestOptions
) {
  return request<PurchaseExceptionRecord>(
    `/bff/purchase/exceptions/${exceptionUuid}/handle`,
    {
      method: "POST",
      data,
      ...(options || {}),
    }
  );
}
